import React, { useState } from 'react';
import MainNavigation from '../../components/ui/MainNavigation';
import Footer from '../home-page/components/Footer';
import Icon from '../../components/AppIcon';
import Select from '../../components/ui/Select';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import RegulationCard from './components/RegulationCard';
import ComparisonTool from './components/ComparisonTool';
import StateSelector from './components/StateSelector';

const StateTowingRegulations = () => {
  const [selectedState, setSelectedState] = useState('TX');
  const [searchTerm, setSearchTerm] = useState('');
  const [bookmarkedStates, setBookmarkedStates] = useState(['TX', 'CA']);
  const [showComparison, setShowComparison] = useState(false);

  const stateRegulations = {
    TX: {
      name: 'Texas',
      weightLimit: '26,000 lbs GVWR without CDL',
      licenseRequirements: 'Class C license for trailers under 26,000 lbs GVWR',
      speedRestrictions: '70 mph on highways, 60 mph with trailer',
      brakeRequirements: 'Required for trailers over 4,500 lbs',
      lightingRequirements: 'Tail lights, brake lights, turn signals, and side markers required',
      safetyChains: 'Required for all trailers, must be crossed under hitch',
      registrationRequired: true,
      effectiveDate: '2025-01-01',
      penalties: 'Fines up to $500 for non-compliance',
      officialResource: 'https://www.txdmv.gov'
    },
    CA: {
      name: 'California',
      weightLimit: '10,000 lbs without special endorsement',
      licenseRequirements: 'Class C license for trailers under 10,000 lbs, Class A for heavier',
      speedRestrictions: '55 mph maximum with trailer',
      brakeRequirements: 'Required for trailers over 3,000 lbs',
      lightingRequirements: 'Full lighting system including reflectors and clearance lights',
      safetyChains: 'Required, must support full trailer weight',
      registrationRequired: true,
      effectiveDate: '2024-07-01',
      penalties: 'Fines up to $1,000 for violations',
      officialResource: 'https://www.dmv.ca.gov'
    },
    FL: {
      name: 'Florida',
      weightLimit: '26,000 lbs GVWR without CDL',
      licenseRequirements: 'Class E license sufficient for most recreational trailers',
      speedRestrictions: '70 mph on highways, reduced in construction zones',
      brakeRequirements: 'Required for trailers over 3,000 lbs',
      lightingRequirements: 'Standard tail, brake, and turn signal lights required',
      safetyChains: 'Required for all trailers',
      registrationRequired: true,
      effectiveDate: '2025-03-15',
      penalties: 'Fines vary by violation, up to $500',
      officialResource: 'https://www.flhsmv.gov'
    },
    NY: {
      name: 'New York',
      weightLimit: '10,000 lbs GVWR without commercial license',
      licenseRequirements: 'Class D license for trailers under 10,000 lbs',
      speedRestrictions: '55 mph maximum with trailer',
      brakeRequirements: 'Required for trailers over 3,000 lbs',
      lightingRequirements: 'Full lighting system with reflectors required',
      safetyChains: 'Required, must be rated for trailer weight',
      registrationRequired: true,
      effectiveDate: '2024-09-01',
      penalties: 'Fines up to $750 for safety violations',
      officialResource: 'https://www.dmv.ny.gov'
    },
    AZ: {
      name: 'Arizona',
      weightLimit: '26,000 lbs GVWR without CDL',
      licenseRequirements: 'Class D license for non-commercial trailers',
      speedRestrictions: '65 mph on highways with trailer',
      brakeRequirements: 'Required for trailers over 3,000 lbs',
      lightingRequirements: 'Tail lights, brake lights, and turn signals required',
      safetyChains: 'Required for all trailers',
      registrationRequired: true,
      effectiveDate: '2025-01-15',
      penalties: 'Fines up to $500 for equipment violations',
      officialResource: 'https://www.azdot.gov'
    }
  };

  const currentRegulation = stateRegulations?.[selectedState];

  const regulationCategories = [
    {
      title: 'Driver\'s License Requirements',
      icon: 'CreditCard',
      content: currentRegulation?.licenseRequirements,
      color: 'text-primary'
    },
    {
      title: 'Weight Limits',
      icon: 'Weight',
      content: currentRegulation?.weightLimit,
      color: 'text-accent'
    },
    {
      title: 'Speed Restrictions',
      icon: 'Gauge',
      content: currentRegulation?.speedRestrictions,
      color: 'text-warning'
    },
    {
      title: 'Brake System Requirements',
      icon: 'CircleStop',
      content: currentRegulation?.brakeRequirements,
      color: 'text-error'
    },
    {
      title: 'Lighting Regulations',
      icon: 'Lightbulb',
      content: currentRegulation?.lightingRequirements,
      color: 'text-success'
    },
    {
      title: 'Safety Chains & Equipment',
      icon: 'Link',
      content: currentRegulation?.safetyChains,
      color: 'text-primary'
    }
  ];

  const toggleBookmark = (state) => {
    setBookmarkedStates(prev => 
      prev?.includes(state) 
        ? prev?.filter(s => s !== state)
        : [...prev, state]
    );
  };

  const filteredCategories = regulationCategories?.filter(cat =>
    cat?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    cat?.content?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation isAuthenticated={false} />
      
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <Icon name="Scale" size={32} className="text-primary" strokeWidth={2} />
              <h1 className="font-heading text-h1 text-foreground">State Towing Regulations</h1>
            </div>
            <p className="font-body text-lg text-muted-foreground max-w-3xl">
              Comprehensive legal compliance information for trailer rentals across all US states. 
              Select your state to view specific requirements, weight limits, and safety regulations.
            </p>
          </div>

          {/* State Selector and Search */}
          <div className="bg-card rounded-lg shadow-elevation-2 p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="md:col-span-1">
                <StateSelector
                  selectedState={selectedState}
                  onStateChange={setSelectedState}
                  bookmarkedStates={bookmarkedStates}
                  onToggleBookmark={toggleBookmark}
                />
              </div>
              
              <div className="md:col-span-2">
                <Input
                  type="text"
                  label="Search Regulations"
                  placeholder="Search by keyword (e.g., brake, license, speed)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-3 items-center justify-between">
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Icon name="Info" size={16} strokeWidth={2} />
                <span className="font-caption text-sm">
                  Effective Date: {currentRegulation?.effectiveDate}
                </span>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                iconName="GitCompare"
                onClick={() => setShowComparison(!showComparison)}
              >
                {showComparison ? 'Hide' : 'Show'} Comparison Tool
              </Button>
            </div>
          </div>

          {/* Comparison Tool */}
          {showComparison && (
            <div className="mb-8">
              <ComparisonTool 
                stateRegulations={stateRegulations}
                initialStates={[selectedState]}
              />
            </div>
          )}

          {/* Current State Info Banner */}
          <div className="bg-primary/10 border-l-4 border-primary rounded-md p-4 mb-6">
            <div className="flex items-start space-x-3">
              <Icon name="MapPin" size={20} className="text-primary mt-1" strokeWidth={2} />
              <div>
                <h3 className="font-heading font-semibold text-foreground mb-1">
                  Viewing Regulations for {currentRegulation?.name}
                </h3>
                <p className="font-body text-sm text-muted-foreground">
                  Registration Required: {currentRegulation?.registrationRequired ? 'Yes' : 'No'} | 
                  Penalties: {currentRegulation?.penalties}
                </p>
              </div>
            </div>
          </div>

          {/* Regulation Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {filteredCategories?.map((category, index) => (
              <RegulationCard
                key={index}
                title={category?.title}
                icon={category?.icon}
                content={category?.content}
                color={category?.color}
              />
            ))}
          </div>

          {filteredCategories?.length === 0 && (
            <div className="text-center py-12">
              <Icon name="SearchX" size={48} className="text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
              <p className="font-body text-muted-foreground">No regulations found matching your search.</p>
            </div>
          )}

          {/* Official Resources */}
          <div className="bg-card rounded-lg shadow-elevation-2 p-6 mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <Icon name="ExternalLink" size={24} className="text-primary" strokeWidth={2} />
              <h2 className="font-heading text-h3 text-foreground">Official State Resources</h2>
            </div>
            <p className="font-body text-muted-foreground mb-4">
              For the most current and detailed information, please visit the official state DMV website:
            </p>
            <a
              href={currentRegulation?.officialResource}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 transition-smooth font-body font-medium"
            >
              <span>{currentRegulation?.name} DMV Website</span>
              <Icon name="ArrowRight" size={16} strokeWidth={2} />
            </a>
          </div>

          {/* Legal Disclaimer */}
          <div className="bg-warning/10 border border-warning/30 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" size={24} className="text-warning mt-1" strokeWidth={2} />
              <div>
                <h3 className="font-heading font-semibold text-foreground mb-2">Legal Disclaimer</h3>
                <p className="font-body text-sm text-muted-foreground mb-2">
                  The information provided on this page is for general informational purposes only and should not be 
                  considered legal advice. Towing regulations are subject to change and may vary by jurisdiction.
                </p>
                <p className="font-body text-sm text-muted-foreground">
                  <strong>Important:</strong> Always verify current regulations with your state's Department of Motor 
                  Vehicles or appropriate authority before operating a trailer. Draggin Trailers is not responsible for 
                  any violations or penalties resulting from outdated or inaccurate information.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default StateTowingRegulations;